import { currentPlatform } from '~/utils/apostatudo/platform-gate';

/**
 * Headers de TODA chamada ao core (`getApostatudoApiUrl`): identidade e plataforma.
 *
 * ERA `getApostatudoAuthHeaders`. Virou "request headers" quando a plataforma entrou
 * (ENG-824) porque agora são duas coisas, e um nome que diz "auth" esconderia a segunda —
 * a próxima pessoa a criar um adapter copiaria o vizinho sem perceber que o header de
 * plataforma existe.
 *
 * ---
 *
 * AUTENTICAÇÃO. Os adapters chamam o core com fetch cru e, até o ENG-799, não mandavam o
 * token do usuário — o core não tinha como saber quem chamava. A proteção dessas rotas é
 * o token: o front sempre manda o Bearer do logado e o core decide (401 para quem não
 * pode). Mesma fonte do `_cactus_core/ApiService` (useAuthStore().accessToken → cookie
 * jwt_token). Só injeta quando HÁ token: deslogado chama sem header, igual a antes, então
 * rota pública servida ao anônimo não quebra.
 *
 * PLATAFORMA (ENG-824). Só o front distingue a WebView do app de um navegador de celular
 * — o User-Agent não —, então ele conta. Vale SÓ para o core: a Cactus tem o vocabulário
 * dela no caminho dela (`_cactus_core/ApiService`), que é restrito por ruleset e onde
 * nada disto encosta.
 *
 * O core trata este header como REGRA DE PRODUTO, nunca como segurança: ele é escrito
 * pelo cliente, logo falsificável. Header ausente vale `web_desktop` lá.
 */
export function getApostatudoRequestHeaders(): Record<string, string> {
  return {
    ...authHeaders(),
    'X-App-Source': currentPlatform(isDesktopViewport()),
  };
}

function authHeaders(): Record<string, string> {
  try {
    const { accessToken } = useAuthStore();

    return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  } catch {
    // Pinia fora de contexto (ex.: chamada muito cedo no boot) — sem token, segue sem
    // header em vez de derrubar a chamada.
    return {};
  }
}

/**
 * O MESMO 1024 do `isDesktop` de `useLayout()`, e não o composable em si: isto roda em
 * adapters, fora de componente, onde `useWindowSize` registraria listeners que ninguém
 * desmonta. Duplicar o número é o preço; o comentário é o que impede os dois de
 * divergirem.
 *
 * SSR e qualquer erro de leitura caem em `false` → `web_mobile`. É o lado seguro: a
 * plataforma que mais importa nesta feature é o app, e nenhum dos dois valores de
 * navegador pode ser confundido com ele.
 */
function isDesktopViewport(): boolean {
  try {
    return typeof window !== 'undefined' && window.innerWidth >= 1024;
  } catch {
    return false;
  }
}
